// The live preview every face window shares: a 4:3 frame with the mirrored
// camera image, an oval guide, an optional big countdown digit, a prompt bar
// and a progress bar. Purely presentational — the owner drives the camera via
// useFaceCapture and passes `videoRef` / `previewStyle` through.
import React from "react";
import { Camera, CameraOff, Loader2 } from "lucide-react";
import { CAMERA_STATUS } from "../../pages/(User)attendance_page/hooks/useFaceCapture";

export const FACE_ACCENT = "#2e7cba";

export default function FaceCameraView({
  videoRef,
  previewStyle,
  status,
  prompt,            // text shown at the bottom of the frame
  countdown,         // 3 / 2 / 1 shown big in the middle, or null
  progress,          // 0..1 or null — bar under the frame
  guideColor = FACE_ACCENT,
  busyLabel,         // overlay while "verifying"/"saving" (spinner + text)
  errorLabel,        // overlay when the camera is unavailable
  onRetryCamera,
  retryLabel,
  dark = false,
  maxWidth = 420,
  children,
}) {
  const ready = status === CAMERA_STATUS.READY;
  const starting = status === CAMERA_STATUS.STARTING || status === CAMERA_STATUS.IDLE;
  const failed = !ready && !starting;

  return (
    <div style={{ width: "100%", maxWidth, margin: "0 auto" }}>
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "4 / 3",
          borderRadius: 18,
          overflow: "hidden",
          background: dark ? "#0b1220" : "#0f172a",
          boxShadow: dark ? "0 10px 30px rgba(0,0,0,0.5)" : "0 10px 30px rgba(15,23,42,0.18)",
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "cover", display: ready ? "block" : "none",
            ...previewStyle,
          }}
        />

        {/* Oval guide: everything outside the ellipse is dimmed. */}
        {ready && (
          <svg viewBox="0 0 400 300" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
            <path
              d="M0 0 H400 V300 H0 Z M200 36 C 262 36 300 96 300 160 C 300 228 258 274 200 274 C 142 274 100 228 100 160 C 100 96 138 36 200 36 Z"
              fill="rgba(0,0,0,0.5)"
              fillRule="evenodd"
            />
            <path
              d="M200 36 C 262 36 300 96 300 160 C 300 228 258 274 200 274 C 142 274 100 228 100 160 C 100 96 138 36 200 36 Z"
              fill="none"
              stroke={guideColor}
              strokeWidth="3"
              strokeDasharray={countdown ? "10 8" : "0"}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        )}

        {/* Countdown digit */}
        {ready && countdown != null && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
            <span style={{ fontSize: 96, fontWeight: 900, color: "#fff", textShadow: "0 4px 24px rgba(0,0,0,0.6)", fontFamily: "'Cairo', sans-serif", lineHeight: 1 }}>
              {countdown}
            </span>
          </div>
        )}

        {/* Prompt bar */}
        {ready && prompt && (
          <div style={{ position: "absolute", left: 12, right: 12, bottom: 12, display: "flex", justifyContent: "center", pointerEvents: "none" }}>
            <span style={{ background: "rgba(15,23,42,0.78)", color: "#fff", padding: "8px 16px", borderRadius: 999, fontSize: 15, fontWeight: 700, fontFamily: "'Cairo', sans-serif", textAlign: "center", maxWidth: "100%" }}>
              {prompt}
            </span>
          </div>
        )}

        {/* Busy overlay (verifying / saving) */}
        {ready && busyLabel && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, background: "rgba(15,23,42,0.55)", color: "#fff" }}>
            <Loader2 size={40} style={{ animation: "spin 1s linear infinite" }} />
            <span style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Cairo', sans-serif" }}>{busyLabel}</span>
          </div>
        )}

        {/* Starting */}
        {starting && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, color: "#cbd5e1" }}>
            <Camera size={44} style={{ opacity: 0.8 }} />
            <Loader2 size={24} style={{ animation: "spin 1s linear infinite" }} />
          </div>
        )}

        {/* Camera failed */}
        {failed && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: 20, color: "#e2e8f0", textAlign: "center" }}>
            <CameraOff size={44} color="#f87171" />
            <span style={{ fontSize: 14, lineHeight: 1.6, fontFamily: "'Cairo', sans-serif" }}>{errorLabel}</span>
            {onRetryCamera && (
              <button type="button" onClick={onRetryCamera} style={{ marginTop: 4, padding: "9px 18px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.3)", background: "rgba(255,255,255,0.1)", color: "#fff", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                {retryLabel}
              </button>
            )}
          </div>
        )}

        {children}
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>

      {/* Progress bar */}
      {progress != null && (
        <div style={{ marginTop: 10, height: 8, borderRadius: 999, background: dark ? "rgba(255,255,255,0.1)" : "rgba(15,23,42,0.08)", overflow: "hidden" }} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
          <div style={{ height: "100%", width: `${Math.min(100, Math.max(0, progress * 100))}%`, background: guideColor, transition: "width 120ms linear", borderRadius: 999 }} />
        </div>
      )}
    </div>
  );
}
